import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, ChevronUp, Sparkles, Undo2 } from "lucide-react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useRestrictedWords } from "@/shared/cache";
import type { Matter } from "@/shared/cache/slices/matters";
import { expandAbbreviationAtCursor } from "@/shared/utils/abbreviations";
import { isNamanHowell } from "@/shared/utils/firm";
import {
	matterDisplayLabel,
	matterNumber,
	matterSearchFields,
	matterSearchValue,
} from "@/shared/utils/matterLabels";
import { getRecentMatters, searchMatters } from "@/shared/utils/matterSearch";
import { useApi } from "@/shared/utils/request";
import {
	detectRestrictedWords,
	mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import type { SearchableField } from "@/shared/utils/smartSearch";
import {
	isValidUnitsForMatter,
	isZurichMatter,
	STANDARD_UNITS_ERROR,
	snapUnitsForMatter,
	stepUnitsDown,
	stepUnitsUp,
	ZURICH_UNITS_ERROR,
} from "@/shared/utils/zurich";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import { ComboBox } from "@/v2/components/ui/combobox";
import { Input } from "@/v2/components/ui/input";
import { Label } from "@/v2/components/ui/label";
import { actingHeaders } from "@/v2/hooks/actions/types";
import { cn } from "@/v2/lib/utils";
import { NarrativeField, type NarrativeFieldHandle } from "./NarrativeField";
import {
	renderActivityItem,
	renderMatterItem,
	renderSelectedMatter,
	renderTaskItem,
} from "./pickerItems";
import {
	defaultFormValues,
	type TimeEntryFormValues,
	timeEntrySchema,
} from "./schema";
import type { Activity, Task } from "./useCodeOptions";
import { useActivityOptions, useTaskOptions } from "./useCodeOptions";
import { useEntryFormContext } from "./useEntryFormContext";

// ── Types ──────────────────────────────────────────────────────────

export type EntryFormHandle = {
	submitExport: () => void;
	submitDuplicate: () => void;
};

export type EntryFormProps = {
	/** HTML id for the form element — allows external buttons to submit via form="id" */
	formId?: string;
	defaultValues?: Partial<TimeEntryFormValues>;
	autoPopulateLastUsedMatter?: boolean;
	onSubmit: (values: TimeEntryFormValues) => void;
	onExport?: (values: TimeEntryFormValues) => void;
	onDuplicate?: (values: TimeEntryFormValues) => void;
	documentName?: string;
	validateOnMount?: boolean;
	readOnly?: boolean;
	/** Banners displayed above the form fields (e.g. feedback, export errors) */
	banners?: React.ReactNode;
	/** Professional whose settings and permissions should drive the form. */
	timekeeperId?: string | null;
	/**
	 * The entry's already-known matter, used to render the matter picker's
	 * selected value instantly while the full matters list loads. Without it the
	 * picker looks blank for the duration of the fetch even though the entry has
	 * a matter. Edit mode only.
	 */
	selectedMatter?: Matter | null;
};

// ── Component ──────────────────────────────────────────────────────

function DatePickerField({
	value,
	onChange,
}: {
	value?: Date;
	onChange: (value: Date) => void;
}) {
	const selectedDate = value ?? new Date();

	return (
		<DatePicker
			date={selectedDate}
			onDateChange={onChange}
			selectionMode="single"
		/>
	);
}

export const EntryForm = React.forwardRef<EntryFormHandle, EntryFormProps>(
	function EntryForm(
		{
			formId = "entry-form",
			defaultValues: defaultValuesProp,
			autoPopulateLastUsedMatter = false,
			onSubmit,
			onExport,
			onDuplicate,
			documentName,
			validateOnMount = false,
			readOnly = false,
			banners,
			timekeeperId,
			selectedMatter,
		},
		ref,
	) {
		const {
			matters,
			settings,
			abbreviationMap,
			abbreviationsEnabled,
			isLoading: contextLoading,
			error: contextError,
		} = useEntryFormContext(timekeeperId);

		const {
			handleSubmit,
			control,
			watch,
			setValue,
			setError,
			getValues,
			clearErrors,
			trigger,
			formState: { errors },
		} = useForm<TimeEntryFormValues>({
			resolver: zodResolver(timeEntrySchema),
			defaultValues: {
				...defaultFormValues,
				date: new Date(),
				...defaultValuesProp,
			},
		});

		const matterId = watch("matter_id");
		const {
			tasks,
			required: taskRequiredRaw,
			isLoading: tasksLoading,
		} = useTaskOptions(matterId, timekeeperId);
		const {
			activities,
			required: activityRequiredRaw,
			isLoading: activitiesLoading,
		} = useActivityOptions(matterId, timekeeperId);
		const taskValue = watch("task");
		const activityValue = watch("activity");
		const duration = watch("duration");
		const description = watch("description");

		const { getRestrictedWords } = useRestrictedWords();

		React.useEffect(() => {
			if (contextError) toast.error(contextError);
		}, [contextError]);

		const restrictedWords = React.useMemo(() => {
			if (!settings.check_for_red_words) return [];
			if (!matterId) return getRestrictedWords();
			const clientCode = matterId.split(".")[0];
			const matterCode = matterId.split(".").slice(1).join(".");
			return getRestrictedWords(clientCode, matterCode);
		}, [settings.check_for_red_words, matterId, getRestrictedWords]);

		const { makeApiRequest } = useApi();

		const handleGenerate = React.useCallback(async (): Promise<string> => {
			const desc = getValues("description");
			const date = getValues("date");

			if (!desc || desc.trim().length < 20) {
				toast.error("Please enter at least 20 characters before generating.");
				throw new Error("Description too short");
			}

			const response = await makeApiRequest({
				endpoint: "/v2/entries/generate-description",
				method: "POST",
				body: { description: desc, date },
				headers: actingHeaders(timekeeperId),
			});

			if (
				!response.data ||
				typeof response.data !== "string" ||
				!response.data.trim()
			) {
				toast.error("Failed to generate description. Please try manually.");
				throw new Error("Invalid AI response");
			}

			return response.data;
		}, [getValues, makeApiRequest, timekeeperId]);

		// Clear validation errors when watched fields change
		// biome-ignore lint/correctness/useExhaustiveDependencies: This intentionally clears only when the watched field value changes.
		React.useEffect(() => {
			if (errors.matter_id && !matterId) clearErrors("matter_id");
		}, [matterId]); // eslint-disable-line react-hooks/exhaustive-deps

		// biome-ignore lint/correctness/useExhaustiveDependencies: This intentionally clears only when the watched field value changes.
		React.useEffect(() => {
			if (errors.duration) clearErrors("duration");
		}, [duration]); // eslint-disable-line react-hooks/exhaustive-deps

		// biome-ignore lint/correctness/useExhaustiveDependencies: This intentionally clears only when the watched field value changes.
		React.useEffect(() => {
			if (errors.task) clearErrors("task");
		}, [taskValue]); // eslint-disable-line react-hooks/exhaustive-deps

		// biome-ignore lint/correctness/useExhaustiveDependencies: This intentionally clears only when the watched field value changes.
		React.useEffect(() => {
			if (errors.activity) clearErrors("activity");
		}, [activityValue]); // eslint-disable-line react-hooks/exhaustive-deps

		// ── Derived data ───────────────────────────────────────────────

		// Requiredness comes from the backend /code-options response (scoped to the
		// selected matter). Default to required while the query is in flight so the
		// submit guard and validate-on-mount never act on a not-yet-known value.
		const taskRequired = tasksLoading ? true : taskRequiredRaw;
		const activityRequired = activitiesLoading ? true : activityRequiredRaw;

		// Reconcile the selected task/activity against a matter's group. The backend
		// owns the mapping: given the current codes and a matter, it returns the
		// equivalent code in that matter's group (preserving e.g. "L120" when the
		// group has it) or null. The UI just sets whatever comes back. Runs on every
		// matter change AND once on open — so a stored code from a different group
		// (e.g. a matterless entry whose codes live outside the firm-default group)
		// resolves to the group the picker is showing instead of rendering blank.
		const reconcileCodesForMatter = React.useCallback(
			async (nextMatterId: string) => {
				const currentTask = getValues("task");
				const currentActivity = getValues("activity");
				if (!currentTask && !currentActivity) return;

				const res = await makeApiRequest({
					endpoint: "/v2/code-options/remap",
					method: "GET",
					queryParams: {
						...(nextMatterId ? { matterId: nextMatterId } : {}),
						...(currentTask ? { taskCode: currentTask } : {}),
						...(currentActivity ? { activityCode: currentActivity } : {}),
					},
					headers: actingHeaders(timekeeperId),
				});
				if (res.status === "ERROR") return;

				const data = res.data as {
					taskCode: string | null;
					activityCode: string | null;
				} | null;
				setValue("task", data?.taskCode ?? "");
				setValue("activity", data?.activityCode ?? "");
			},
			[makeApiRequest, getValues, setValue, timekeeperId],
		);

		// On open, reconcile the entry's stored codes against its matter group so
		// the pickers can display them (e.g. a matterless entry's codes resolve to
		// the firm-default group). Once per mount; later changes use the handlers.
		const didMountReconcile = React.useRef(false);
		React.useEffect(() => {
			if (didMountReconcile.current) return;
			didMountReconcile.current = true;
			void reconcileCodesForMatter(getValues("matter_id"));
		}, [reconcileCodesForMatter, getValues]);

		// Validate on mount — deferred until context has loaded so requirements reflect settled data.
		const hasValidatedOnMount = React.useRef(false);
		// biome-ignore lint/correctness/useExhaustiveDependencies: The ref gates validation to once after async option data settles.
		React.useEffect(() => {
			if (
				!validateOnMount ||
				contextLoading ||
				tasksLoading ||
				activitiesLoading ||
				hasValidatedOnMount.current
			)
				return;
			hasValidatedOnMount.current = true;
			trigger(["matter_id", "duration", "description"]);
			const selected = matters.find(
				(m) => m.matter_id === getValues("matter_id"),
			);
			if (selected?.can_select === false) {
				setError("matter_id", {
					type: "manual",
					message: "Unapproved timekeeper",
				});
			}
			if (taskRequired && !getValues("task")) {
				setError("task", { type: "manual", message: "Task is required" });
			}
			if (activityRequired && !getValues("activity")) {
				setError("activity", {
					type: "manual",
					message: "Activity is required",
				});
			}
		}, [contextLoading, tasksLoading, activitiesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

		// Deduplicate tasks
		const uniqueTasks = React.useMemo(() => {
			const seen = new Set<string>();
			const result = tasks.filter((t) => {
				const key = t.taskCode;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});

			result.sort((a, b) => {
				const aL = a.task.startsWith("L");
				const bL = b.task.startsWith("L");
				const aE = a.task.startsWith("E");
				const bE = b.task.startsWith("E");
				if (aL && !bL) return -1;
				if (!aL && bL) return 1;
				if (aE && !bE) return 1;
				if (!aE && bE) return -1;
				return 0;
			});

			return result;
		}, [tasks]);

		// Only show L-codes by default
		const taskDefaultLimit = React.useMemo(() => {
			const lCodeCount = uniqueTasks.filter((t) =>
				t.task.startsWith("L"),
			).length;
			return lCodeCount || undefined;
		}, [uniqueTasks]);

		const uniqueActivities = React.useMemo(() => {
			const seen = new Set<string>();
			const result = activities.filter((a) => {
				const key = a.activityCode;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});

			result.sort((a, b) => {
				const aIsA = a.activity.startsWith("A");
				const bIsA = b.activity.startsWith("A");
				if (aIsA && !bIsA) return -1;
				if (!aIsA && bIsA) return 1;
				const aNum = parseInt(a.activity.replace(/^\D+/, ""), 10) || 0;
				const bNum = parseInt(b.activity.replace(/^\D+/, ""), 10) || 0;
				return aNum - bNum;
			});

			return result;
		}, [activities]);

		const activityDefaultLimit = React.useMemo(() => {
			const aCodeCount = uniqueActivities.filter((a) =>
				a.activity.startsWith("A"),
			).length;
			return aCodeCount || undefined;
		}, [uniqueActivities]);

		const setCustomError = React.useCallback(
			(field: keyof TimeEntryFormValues, message: string) => {
				setError(field, { type: "manual", message });
			},
			[setError],
		);

		// ── Duration helpers ─────────────────────────────────────────

		const selectedClient = React.useMemo(() => {
			const found = matters.find((m) => m.matter_id === matterId);
			if (found) return found.client ?? "";
			// While the matters list is still loading, fall back to the entry's
			// seeded matter so units snap to the correct billing grid (e.g. Zurich)
			// instead of defaulting to the standard grid and saving wrong units.
			if (selectedMatter && selectedMatter.matter_id === matterId) {
				return selectedMatter.client ?? "";
			}
			return "";
		}, [matters, matterId, selectedMatter]);

		const handleDurationIncrement = React.useCallback(() => {
			const current =
				typeof duration === "number"
					? duration
					: parseFloat(String(duration)) || 0;
			setValue("duration", stepUnitsUp(current, selectedClient));
		}, [duration, selectedClient, setValue]);

		const handleDurationDecrement = React.useCallback(() => {
			const current =
				typeof duration === "number"
					? duration
					: parseFloat(String(duration)) || 0;
			setValue("duration", stepUnitsDown(current, selectedClient));
		}, [duration, selectedClient, setValue]);

		// ── Matter change handler ──────────────────────────────────────

		const handleMatterSelect = React.useCallback(
			(newMatterId: string) => {
				const selected = matters.find((m) => m.matter_id === newMatterId);
				if (!selected) return;

				setValue("matter_id", selected.matter_id);
				setValue("matter", matterNumber(selected));
				if (selected.can_select === false) {
					setCustomError("matter_id", "Unapproved timekeeper");
				} else {
					clearErrors("matter_id");
				}

				// Snap duration to the new matter's grid (handles Zurich → standard and back)
				const currentDuration = getValues("duration");
				if (typeof currentDuration === "number" && currentDuration > 0) {
					setValue(
						"duration",
						snapUnitsForMatter(currentDuration, selected.client),
					);
				}
			},
			[matters, setValue, getValues, setCustomError, clearErrors],
		);

		const didAutoPopulateLastUsedMatter = React.useRef(false);
		const didUserChangeMatter = React.useRef(false);
		const previousTimekeeperId = React.useRef(timekeeperId);
		const pendingTimekeeperValidation = React.useRef(false);
		React.useEffect(() => {
			if (previousTimekeeperId.current === timekeeperId) return;
			previousTimekeeperId.current = timekeeperId;
			pendingTimekeeperValidation.current = true;
			didAutoPopulateLastUsedMatter.current = false;
			didUserChangeMatter.current = false;
			clearErrors("matter_id");
		}, [clearErrors, timekeeperId]);

		React.useEffect(() => {
			if (!pendingTimekeeperValidation.current || contextLoading) return;
			pendingTimekeeperValidation.current = false;
			const currentMatterId = getValues("matter_id");
			const currentMatter = matters.find(
				(matter) => matter.matter_id === currentMatterId,
			);
			if (currentMatter && currentMatter.can_select !== false) {
				didAutoPopulateLastUsedMatter.current = true;
				return;
			}

			setValue("matter_id", "");
			setValue("matter", null);
			setValue("task", "");
			setValue("activity", "");
			const lastUsedMatter = settings.auto_populate_last_used_matter
				? matters.find(
						(matter) =>
							matter.matter_id === settings.last_used_matter &&
							matter.can_select !== false,
					)
				: undefined;
			if (lastUsedMatter) {
				setValue("matter_id", lastUsedMatter.matter_id);
				setValue("matter", matterNumber(lastUsedMatter));
			}
			didAutoPopulateLastUsedMatter.current = true;
		}, [contextLoading, getValues, matters, setValue, settings]);

		React.useEffect(() => {
			if (
				!autoPopulateLastUsedMatter ||
				didAutoPopulateLastUsedMatter.current ||
				contextLoading
			) {
				return;
			}

			if (matterId || didUserChangeMatter.current) {
				didAutoPopulateLastUsedMatter.current = true;
				return;
			}

			const matterToUse = settings.auto_populate_last_used_matter
				? settings.last_used_matter
				: "";
			if (!matterToUse || matters.length === 0) return;

			didAutoPopulateLastUsedMatter.current = true;
			const selected = matters.find((m) => m.matter_id === matterToUse);
			if (selected) {
				setValue("matter_id", selected.matter_id);
				setValue("matter", matterNumber(selected));
			}
		}, [
			autoPopulateLastUsedMatter,
			matterId,
			settings.auto_populate_last_used_matter,
			settings.last_used_matter,
			matters,
			contextLoading,
			setValue,
		]);

		// ── Search field configs ───────────────────────────────────────

		const taskFields: ((item: Task) => SearchableField)[] = React.useMemo(
			() => [
				(t: Task) => ({ value: t.task, weight: 1.5 }),
				(t: Task) => ({ value: t.taskDescription, weight: 1.0 }),
			],
			[],
		);

		const activityFields: ((item: Activity) => SearchableField)[] =
			React.useMemo(
				() => [
					(a: Activity) => ({ value: a.activity, weight: 1.5 }),
					(a: Activity) => ({ value: a.activityDescription, weight: 1.0 }),
				],
				[],
			);

		// ── Form submit ────────────────────────────────────────────────

		const validateAndPrepare = React.useCallback(
			(values: TimeEntryFormValues): TimeEntryFormValues | null => {
				const selectedMatter = matters.find(
					(m) => m.matter_id === values.matter_id,
				);
				if (selectedMatter?.can_select === false) {
					setCustomError("matter_id", "Unapproved timekeeper");
					return null;
				}

				if (taskRequired && !values.task) {
					setCustomError("task", "Task is required");
					return null;
				}
				if (activityRequired && !values.activity) {
					setCustomError("activity", "Activity is required");
					return null;
				}

				// Validate unit increment against the matter's grid
				const submitClient = selectedMatter?.client ?? "";
				if (!isValidUnitsForMatter(values.duration, submitClient)) {
					const message = isZurichMatter(submitClient)
						? ZURICH_UNITS_ERROR
						: STANDARD_UNITS_ERROR;
					setCustomError("duration", message);
					return null;
				}

				const prepared = { ...values };
				prepared.date =
					prepared.date instanceof Date
						? prepared.date
						: new Date(prepared.date);

				// Only clear when a matter IS selected and its firm rules say the code
				// isn't required. Null matter keeps any user-provided codes — the
				// picker is enabled in that case so the value is intentional.
				if (values.matter_id && !taskRequired) prepared.task = "";
				if (values.matter_id && !activityRequired && !isNamanHowell())
					prepared.activity = "";

				return prepared;
			},
			[taskRequired, activityRequired, matters, setCustomError],
		);

		const onFormSubmit = React.useCallback(
			(values: TimeEntryFormValues) => {
				if (contextLoading || contextError) return;
				// Block submission if restricted words with "block" severity are present
				if (restrictedWords.length > 0 && values.description) {
					const matches = mergeOverlappingMatches(
						detectRestrictedWords(values.description, restrictedWords),
					);
					if (matches.some((m) => m.severity === "block")) {
						setError("description", {
							message: "Please fix restricted words before submitting",
						});
						return;
					}
				}

				const prepared = validateAndPrepare(values);
				if (!prepared) return;
				onSubmit(prepared);
			},
			[
				validateAndPrepare,
				onSubmit,
				restrictedWords,
				setError,
				contextLoading,
				contextError,
			],
		);

		const onFormExport = React.useCallback(
			(values: TimeEntryFormValues) => {
				if (!onExport) return;
				if (contextLoading || contextError) return;
				if (restrictedWords.length > 0 && values.description) {
					const matches = mergeOverlappingMatches(
						detectRestrictedWords(values.description, restrictedWords),
					);
					if (matches.some((m) => m.severity === "block")) {
						setError("description", {
							message: "Please fix restricted words before submitting",
						});
						return;
					}
				}
				const prepared = validateAndPrepare(values);
				if (!prepared) return;
				onExport(prepared);
			},
			[
				validateAndPrepare,
				onExport,
				restrictedWords,
				setError,
				contextLoading,
				contextError,
			],
		);

		const onFormDuplicate = React.useCallback(
			(values: TimeEntryFormValues) => {
				if (!onDuplicate) return;
				if (contextLoading || contextError) return;
				if (restrictedWords.length > 0 && values.description) {
					const matches = mergeOverlappingMatches(
						detectRestrictedWords(values.description, restrictedWords),
					);
					if (matches.some((m) => m.severity === "block")) {
						setError("description", {
							message: "Please fix restricted words before submitting",
						});
						return;
					}
				}
				const prepared = validateAndPrepare(values);
				if (!prepared) return;
				onDuplicate(prepared);
			},
			[
				validateAndPrepare,
				onDuplicate,
				restrictedWords,
				setError,
				contextLoading,
				contextError,
			],
		);

		React.useImperativeHandle(
			ref,
			() => ({
				submitExport: () => handleSubmit(onFormExport)(),
				submitDuplicate: () => handleSubmit(onFormDuplicate)(),
			}),
			[handleSubmit, onFormExport, onFormDuplicate],
		);

		// ── Cmd/Ctrl+Enter to submit ──────────────────────────────────

		React.useEffect(() => {
			const handler = (e: KeyboardEvent) => {
				if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
					e.preventDefault();
					handleSubmit(onFormSubmit)();
				}
			};
			document.addEventListener("keydown", handler);
			return () => document.removeEventListener("keydown", handler);
		}, [handleSubmit, onFormSubmit]);

		// ── Abbreviation expansion ──────────────────────────────────────

		const narrativeRef = React.useRef<HTMLTextAreaElement | null>(null);
		const narrativeActionRef = React.useRef<NarrativeFieldHandle>(null);
		const [aiState, setAiState] = React.useState({
			isGenerating: false,
			canUndo: false,
		});

		const handleNarrativeChange = React.useCallback(
			(
				e: React.ChangeEvent<HTMLTextAreaElement>,
				fieldOnChange: (value: string) => void,
				previousValue: string,
			) => {
				const nextValue = e.target.value;
				const textarea = narrativeRef.current;
				const expansion = expandAbbreviationAtCursor({
					value: nextValue,
					previousValue,
					cursorPosition: textarea?.selectionStart,
					abbreviationMap: abbreviationsEnabled ? abbreviationMap : null,
				});

				if (!expansion) {
					fieldOnChange(nextValue);
					return;
				}

				fieldOnChange(expansion.value);

				// Fix cursor position after React re-render
				if (textarea) {
					requestAnimationFrame(() => {
						textarea.setSelectionRange(
							expansion.cursorPosition,
							expansion.cursorPosition,
						);
					});
				}
			},
			[abbreviationMap, abbreviationsEnabled],
		);

		// ── Duration input state (allow free typing, parse on blur) ────

		const [durationInput, setDurationInput] = React.useState(
			duration && duration > 0 ? String(duration) : "",
		);

		// Sync external duration changes
		React.useEffect(() => {
			if (duration > 0) {
				setDurationInput(String(duration));
			} else {
				setDurationInput("");
			}
		}, [duration]);

		const handleDurationInputChange = React.useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				setDurationInput(e.target.value);
			},
			[],
		);

		const handleDurationBlur = React.useCallback(() => {
			const parsed = parseFloat(durationInput);
			if (Number.isNaN(parsed) || parsed <= 0) {
				const fallback = snapUnitsForMatter(0, selectedClient);
				setValue("duration", fallback);
				setDurationInput(String(fallback));
				return;
			}
			const snapped = snapUnitsForMatter(parsed, selectedClient);
			setValue("duration", snapped);
			setDurationInput(String(snapped));
		}, [durationInput, selectedClient, setValue]);

		// Live validation — clear or set the duration error as the user types
		React.useEffect(() => {
			if (durationInput === "") return;
			const parsed = parseFloat(durationInput);
			if (Number.isNaN(parsed) || parsed <= 0) return; // blur handler will deal with it
			if (!isValidUnitsForMatter(parsed, selectedClient)) {
				setError("duration", {
					type: "manual",
					message: isZurichMatter(selectedClient)
						? ZURICH_UNITS_ERROR
						: STANDARD_UNITS_ERROR,
				});
			} else {
				clearErrors("duration");
			}
		}, [durationInput, selectedClient, setError, clearErrors]);

		const handleDurationKeyDown = React.useCallback(
			(e: React.KeyboardEvent) => {
				if (e.key === "ArrowUp") {
					e.preventDefault();
					handleDurationIncrement();
				} else if (e.key === "ArrowDown") {
					e.preventDefault();
					handleDurationDecrement();
				}
			},
			[handleDurationIncrement, handleDurationDecrement],
		);

		// ── Render ─────────────────────────────────────────────────────

		return (
			<form id={formId} onSubmit={handleSubmit(onFormSubmit)} className="p-4">
				{/* Don't gate the whole form on `contextLoading`: a disabled <fieldset>
					natively disables every child, so a cold-cache load (e.g. after a hard
					reload) left the duration/narrative fields un-editable until the
					settings/matters/abbreviations query resolved. Those fields don't depend
					on that data, so keep them interactive; the matter/task/activity pickers
					simply render empty until their options arrive, and requiredness +
					validate-on-mount already defer on their own loading flags. */}
				<fieldset
					disabled={readOnly || Boolean(contextError)}
					className="grid min-w-0 gap-4 disabled:opacity-75"
				>
					{/* Status banners (feedback, export errors) */}
					{banners}

					{/* Document name (edit mode only) */}
					{documentName && (
						<div className="grid gap-1">
							<Label>Task Title</Label>
							<p className="text-sm text-muted-foreground leading-snug wrap-break-word rounded-md bg-muted/50 px-3 py-2">
								{documentName}
							</p>
						</div>
					)}

					{/* Matter */}
					<div className="grid w-full gap-1.5">
						<Label htmlFor="matter">Matter</Label>
						<Controller
							control={control}
							name="matter_id"
							render={({ field }) => (
								<ComboBox
									items={matters}
									value={field.value || null}
									onChange={(id) => {
										didUserChangeMatter.current = true;
										field.onChange(id);
										handleMatterSelect(id);
										reconcileCodesForMatter(id);
									}}
									fields={matterSearchFields}
									getDefaultItems={getRecentMatters}
									searchItems={searchMatters}
									getKey={(m) => m.matter_id}
									getDisplayLabel={matterDisplayLabel}
									getSearchValue={matterSearchValue}
									renderItem={renderMatterItem}
									renderSelected={renderSelectedMatter}
									selectedItemFallback={selectedMatter}
									placeholder="Select matter..."
									error={errors.matter_id?.message}
									onBlur={field.onBlur}
									clearable
									onClear={() => {
										didUserChangeMatter.current = true;
										setValue("matter_id", "");
										setValue("matter", null);
										reconcileCodesForMatter("");
									}}
								/>
							)}
						/>
					</div>

					{/* Task + Activity */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
						<div className="grid w-full gap-1.5">
							<Label htmlFor="task">Task</Label>
							<Controller
								control={control}
								name="task"
								render={({ field }) => (
									<ComboBox
										items={uniqueTasks}
										value={(() => {
											if (!field.value) return null;
											const found = uniqueTasks.find(
												(t) => t.taskCode === field.value,
											);
											return found ? found.taskCode : null;
										})()}
										onChange={(key) => {
											const task = uniqueTasks.find((t) => t.taskCode === key);
											field.onChange(task?.taskCode ?? "");
										}}
										fields={taskFields}
										getKey={(t) => t.taskCode}
										getDisplayLabel={(t) => `${t.task} - ${t.taskDescription}`}
										getSearchValue={(t) => t.task}
										renderItem={renderTaskItem}
										renderSelected={renderTaskItem}
										placeholder="Select task..."
										// Only disable when a matter IS selected and its firm rules
										// say a task isn't required — null matter keeps the picker
										// enabled so the user can still pick one optionally.
										disabled={!!matterId && !taskRequired}
										disabledMessage="Not required"
										defaultLimit={taskDefaultLimit}
										clearable
										onClear={() => field.onChange("")}
										error={
											taskRequired && errors.task?.message
												? errors.task.message
												: undefined
										}
										onBlur={field.onBlur}
									/>
								)}
							/>
						</div>

						<div className="grid w-full gap-1.5">
							<Label htmlFor="activity">Activity</Label>
							<Controller
								control={control}
								name="activity"
								render={({ field }) => (
									<ComboBox
										items={uniqueActivities}
										value={(() => {
											if (!field.value) return null;
											const found = uniqueActivities.find(
												(a) => a.activityCode === field.value,
											);
											return found ? found.activityCode : null;
										})()}
										onChange={(key) => {
											const act = uniqueActivities.find(
												(a) => a.activityCode === key,
											);
											field.onChange(act?.activityCode ?? "");
										}}
										fields={activityFields}
										getKey={(a) => a.activityCode}
										getDisplayLabel={(a) =>
											`${a.activity} - ${a.activityDescription}`
										}
										getSearchValue={(a) => a.activity}
										renderItem={renderActivityItem}
										renderSelected={renderActivityItem}
										placeholder="Select activity..."
										// Only disable when a matter IS selected and its firm rules
										// say activity isn't required. Naman Howell exception kept.
										disabled={
											!!matterId && !activityRequired && !isNamanHowell()
										}
										disabledMessage="Not required"
										defaultLimit={activityDefaultLimit}
										clearable
										onClear={() => field.onChange("")}
										error={
											activityRequired && errors.activity?.message
												? errors.activity.message
												: undefined
										}
										onBlur={field.onBlur}
									/>
								)}
							/>
						</div>
					</div>

					{/* Date + Duration */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
						<div className="grid w-full gap-1.5">
							<Label htmlFor="date">Date</Label>
							<Controller
								control={control}
								name="date"
								render={({ field }) => (
									<DatePickerField
										value={field.value}
										onChange={(day) => field.onChange(day)}
									/>
								)}
							/>
							{errors.date && (
								<p className="text-xs text-red-500">{errors.date.message}</p>
							)}
						</div>

						<div className="grid w-full gap-1.5">
							<Label htmlFor="duration">Units</Label>
							<div className="flex gap-1">
								<Input
									id="duration"
									type="text"
									inputMode="decimal"
									autoComplete="off"
									value={durationInput}
									onChange={handleDurationInputChange}
									onBlur={handleDurationBlur}
									onKeyDown={handleDurationKeyDown}
									className={cn(
										"text-foreground bg-card border-border",
										errors.duration && "border-red-500",
									)}
								/>
								<div className="flex flex-col gap-0.5">
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={handleDurationIncrement}
										className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
										tabIndex={-1}
									>
										<ChevronUp className="h-3 w-3" />
									</Button>
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={handleDurationDecrement}
										className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
										tabIndex={-1}
									>
										<ChevronDown className="h-3 w-3" />
									</Button>
								</div>
							</div>
							{errors.duration && (
								<p className="text-xs text-red-500">
									{errors.duration.message}
								</p>
							)}
						</div>
					</div>

					{/* Narrative */}
					<div className="grid w-full gap-1.5">
						<div className="flex items-center justify-between">
							<Label htmlFor="description">Narrative</Label>
							<div className="flex items-center gap-1">
								{aiState.canUndo && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="h-6 text-xs gap-1 px-2 text-muted-foreground"
										onClick={() => narrativeActionRef.current?.undo()}
									>
										<Undo2 className="size-3" />
										Undo
									</Button>
								)}
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="h-6 text-xs gap-1 px-2"
									disabled={
										!description ||
										description.trim().length < 20 ||
										aiState.isGenerating
									}
									onClick={() => narrativeActionRef.current?.generate()}
								>
									{aiState.isGenerating ? (
										<span className="size-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
									) : (
										<Sparkles className="size-3" />
									)}
									{aiState.isGenerating ? "Generating..." : "Generate with AI"}
								</Button>
							</div>
						</div>
						<Controller
							control={control}
							name="description"
							render={({ field }) => (
								<NarrativeField
									value={field.value}
									onChange={(e) =>
										handleNarrativeChange(e, field.onChange, field.value)
									}
									onBlur={field.onBlur}
									error={errors.description?.message}
									restrictedWords={restrictedWords}
									textareaRef={narrativeRef}
									onGenerate={handleGenerate}
									onDirectChange={(val) =>
										setValue("description", val, { shouldDirty: true })
									}
									actionRef={narrativeActionRef}
									onStateChange={setAiState}
								/>
							)}
						/>
					</div>
				</fieldset>
			</form>
		);
	},
);

export default EntryForm;
